/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import constants, {
  DELIVERY_STATUS_DESCRIPTION,
  DELIVERY_STATUS_DOWNLOAD_READY,
  DELIVERY_STATUS_PENDING,
  DELIVERY_STATUS_PREPARING,
  DELIVERY_STATUS_TRACKING_READY,
} from '../../constants';

import { createSelector } from 'reselect';
import format from 'date-fns/format';
import { formatDateAsDDMMYYYY } from '../../lib/dates';
import getBookingAccommodation from './getBookingAccommodation';
import getBookingBreakdownExtras from './getBookingBreakdownExtras';
import getBookingBreakdownTickets from './getBookingBreakdownTickets';
import getBookingDelivery from './getBookingDelivery';
import getBookingInsurance from './getBookingInsurance';
import getBookingNameChanges from './getBookingNameChanges';
import getBookingPrices from './getBookingPrices';
import getBookingTicketDownloads from './getBookingTicketDownloads';
import getBookingTransport from './getBookingTransport';
import getResoldTicketsTotalFee from './resale/getResoldTicketsTotalFee';
import isBookingLoaded from './isBookingLoaded';
import withParams from '../../lib/router/withParams';

const trackingUrl = trackingId =>
  `https://www.royalmail.com/track-your-item#/tracking-results/${trackingId}`;

const downloadUrl = ({ key, external, url }) =>
  external ? url : withParams(constants.DOWNLOAD_URL, { key });

const generateCheckInCheckOutDates = (
  checkin_date = '',
  checkout_date = '',
) => {
  let checkInCheckOutDates = '';
  if (!checkin_date || !checkout_date) {
    return checkInCheckOutDates;
  }

  checkInCheckOutDates = `${formatDateAsDDMMYYYY(
    checkin_date,
  )} - ${formatDateAsDDMMYYYY(checkout_date)}`;

  return checkInCheckOutDates;
};

const getBookingBreakdown = createSelector(
  isBookingLoaded,
  getBookingAccommodation,
  getBookingTransport,
  getBookingBreakdownExtras,
  getBookingBreakdownTickets,
  getBookingInsurance,
  getBookingDelivery,
  getBookingTicketDownloads,
  getResoldTicketsTotalFee,
  getBookingPrices,
  getBookingNameChanges,
  (
    loaded,
    accommodation,
    transport,
    extras,
    tickets,
    insurance,
    delivery,
    downloads,
    resoldTicketFee,
    bookingPrices,
    bookingNameChanges,
  ) => {
    if (!loaded) {
      return [];
    }

    const breakdown = [];

    // Produce Accommodation Breakdown
    if (accommodation.length) {
      const line_items = accommodation.map(
        ({
          name,
          room_name,
          prices,
          provider_handle,
          start_date,
          checkout_date,
        }) => {
          const cost = prices.allocations.value + prices.supplement.value;
          const checkInCheckOutDates = generateCheckInCheckOutDates(
            start_date?.iso_value,
            checkout_date?.iso_value,
          );

          return {
            title: name,
            subtitle: room_name,
            quantity: 1,
            cost,
            provider: provider_handle,
            checkInCheckOutDates,
          };
        },
      );

      breakdown.push({
        title: 'Accommodation',
        type: 'accommodation',
        line_items,
      });
    }

    if (bookingNameChanges?.length) {
      const line_items = bookingNameChanges.map(({ display_amount }) => ({
        title: 'Name Change Fee',
        quantity: 1,
        displayAmount: display_amount,
      }));

      breakdown.push({
        title: 'Customer Change',
        type: 'nameChange',
        line_items,
      });
    }

    // Produce Tickets Breakdown
    if (tickets.length) {
      breakdown.push({
        title: 'Tickets',
        type: 'tickets',
        line_items: tickets,
      });
    }

    // Produce Transport Breakdown
    if (transport.length) {
      const line_items = transport.map(
        ({
          departure_location,
          departure_date,
          is_departure_time_specified,
          arrival_location,
          price,
        }) => ({
          title: `${departure_location.value} → ${arrival_location.value}`,
          subtitle: `${format(departure_date.iso_value, 'Do MMMM YYYY')} - ${
            is_departure_time_specified
              ? format(departure_date.iso_value, 'HH:mm')
              : 'Departure time TBC'
          }`,
          quantity: 1,
          cost: price.value,
        }),
      );

      breakdown.push({
        title: 'Transport',
        type: 'transport',
        line_items,
      });
    }

    // Produce Extras Breakdown
    if (extras.length) {
      breakdown.push({
        title: 'Extras',
        type: 'extras',
        line_items: extras,
      });
    }

    // Produce Insurance Breakdown
    if (insurance.length) {
      const line_items = insurance.map(({ name, price }) => ({
        title: name,
        quantity: 1,
        cost: price.value,
      }));

      breakdown.push({
        title: 'Insurance',
        type: 'insurance',
        line_items,
      });
    }

    // Produce Delivery Breakdown
    if (delivery.length) {
      const line_items = delivery.map(
        ({ delivery_name, delivery_price, status, tracking_id }) => {
          const line = {
            title: delivery_name,
            quantity: 1,
            cost: delivery_price.value,
          };
          if (status.code >= 0) {
            line.subtitle = DELIVERY_STATUS_DESCRIPTION[status.code];
            switch (status.code) {
              case DELIVERY_STATUS_PENDING:
              case DELIVERY_STATUS_PREPARING:
                break;
              case DELIVERY_STATUS_DOWNLOAD_READY:
                if (downloads?.length) {
                  line.links = downloads.map(download => ({
                    text: 'Click here to download',
                    value: downloadUrl(download),
                    external: download.external,
                  }));
                }
                break;
              case DELIVERY_STATUS_TRACKING_READY:
                if (tracking_id) {
                  line.links = [
                    {
                      text: 'Tracking delivery',
                      value: trackingUrl(tracking_id),
                      external: true,
                    },
                  ];
                }
                break;
              default:
                break;
            }
          }
          return line;
        },
      );

      breakdown.push({
        title: 'Delivery',
        type: 'delivery',
        line_items,
      });
    }

    // Display discounts, if we have any.
    if (bookingPrices.discounts.value > 0) {
      breakdown.push({
        title: 'Discounts',
        line_items: [
          {
            title: 'Discount',
            cost: bookingPrices.discounts.value * -1,
          },
        ],
      });
    }

    // Display resale fees if tickets with a booking fee have been resold.
    if (resoldTicketFee > 0) {
      breakdown.push({
        title: 'Fees',
        line_items: [
          {
            title: 'Resale Fees',
            cost: resoldTicketFee,
          },
        ],
      });
    }

    return breakdown;
  },
);

export default getBookingBreakdown;
